/**
 * Service-worker update detection hook — Phase 7a (14 Jun 2026).
 *
 * Tracks whether a newer service worker has finished installing and
 * is waiting to take over, then exposes a callback that activates
 * it (postMessage SKIP_WAITING to the waiting worker, followed by a
 * page reload when the new worker takes control via the SW's
 * `clients.claim()` in its activate handler).
 *
 * Powers UpdateBanner. Without this hook, the SKIP_WAITING handler
 * in public/sw.js is functionally dead — nothing in the app sent
 * the message, so a freshly-deployed SW could sit "waiting" forever
 * while installed-PWA guests ran on stale code. Premium correctness:
 * a deploy must reach every guest within one visit, not "when they
 * happen to close every tab".
 *
 * State machine:
 *
 *   1. Page loads with serviceWorker support → register listeners.
 *   2. If a `waiting` SW already exists at load time AND a controller
 *      is already in place (i.e., this isn't the very first install),
 *      surface immediately. Common when the user previously closed
 *      tabs without applying an update that landed between visits.
 *   3. While the page is open, listen for `updatefound` on the
 *      registration. When the new worker reaches `installed` AND a
 *      controller exists, surface.
 *   4. On `applyUpdate()`, post `{ type: 'SKIP_WAITING' }` to the
 *      waiting worker and arm the reload guard.
 *   5. `controllerchange` fires when the new worker takes control
 *      (sw.js calls `self.clients.claim()` in activate). With the
 *      guard armed, reload the page so the user is now running
 *      against the freshly-activated SW.
 *
 * Why the reload-armed guard:
 *   `controllerchange` can fire for reasons we didn't trigger — most
 *   commonly when ANOTHER tab calls SKIP_WAITING on the same SW.
 *   Reloading unconditionally would yank the rug out mid-interaction
 *   without the user choosing to. The ref ensures we ONLY reload
 *   when THIS tab requested the update via applyUpdate().
 *
 * Why we gate update detection on controller presence:
 *   On the very first PWA install, the SW reaches `installed` with
 *   `navigator.serviceWorker.controller === null` — there's no
 *   previous SW to replace. That's not an "update", and prompting
 *   "Reload to update" during a fresh sign-up flow would confuse and
 *   probably interrupt onboarding. Surface only when there's an old
 *   SW that's actually about to be swapped.
 *
 * Defensive: returns `updateAvailable: false` + no-op `applyUpdate`
 * in any environment without `navigator.serviceWorker` (older mobile
 * browsers, jsdom in tests, iOS Lockdown Mode).
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { reloadPage } from './reload';

export interface UseUpdateAvailableResult {
  /** True when a newer service worker is installed and waiting. */
  updateAvailable: boolean;
  /**
   * Activate the waiting worker and reload onto it. No-op when
   * `updateAvailable` is false. The actual reload happens via the
   * `controllerchange` listener once the new worker takes control.
   */
  applyUpdate: () => void;
}

export function useUpdateAvailable(): UseUpdateAvailableResult {
  const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(
    null,
  );
  // Armed by applyUpdate; gates the controllerchange listener so we
  // only reload when THIS tab triggered the switchover. A ref (not
  // state) because the reload listener is set up once per mount and
  // would otherwise close over a stale value.
  const reloadArmedRef = useRef(false);

  // ─── Detect a waiting / newly-installed SW ─────────────────────
  useEffect(() => {
    if (typeof navigator === 'undefined') return;
    if (!('serviceWorker' in navigator)) return;

    let cancelled = false;

    /**
     * Wire up a statechange listener on an installing worker so we
     * catch the moment it reaches `installed`. The controller-presence
     * check inside the listener distinguishes "first install" from
     * "update": with controller, the new worker is replacing an old
     * one — surface. Without, it's the initial install — silent.
     */
    const trackInstallingWorker = (worker: ServiceWorker) => {
      const handler = () => {
        if (
          worker.state === 'installed' &&
          navigator.serviceWorker.controller &&
          !cancelled
        ) {
          setWaitingWorker(worker);
        }
      };
      worker.addEventListener('statechange', handler);
    };

    void navigator.serviceWorker.getRegistration().then((registration) => {
      if (cancelled || !registration) return;

      // Case A: a waiting SW already exists at load time. Common when
      // the user closed all tabs without applying a previous update
      // and the SW stayed waiting across the gap. Surface immediately.
      if (registration.waiting && navigator.serviceWorker.controller) {
        setWaitingWorker(registration.waiting);
        return;
      }

      // Case B: a SW is mid-install when the page loads. Hook into
      // its statechange so we catch the `installed` transition.
      if (registration.installing) {
        trackInstallingWorker(registration.installing);
      }

      // Case C: a new SW starts installing while the page is open.
      // `updatefound` fires on the registration; the installing
      // worker is then exposed on `registration.installing`. Same
      // statechange tracking as Case B.
      registration.addEventListener('updatefound', () => {
        if (registration.installing) {
          trackInstallingWorker(registration.installing);
        }
      });
    });

    return () => {
      cancelled = true;
    };
  }, []);

  // ─── Reload when the new SW takes control ──────────────────────
  // Lives in its own effect so the listener persists across
  // re-renders during the brief window between applyUpdate() firing
  // and the controllerchange event landing.
  useEffect(() => {
    if (typeof navigator === 'undefined') return;
    if (!('serviceWorker' in navigator)) return;
    // Capture the container reference at setup time. In test
    // teardown the global `navigator.serviceWorker` may be removed
    // *before* React's unmount cleanup runs (Vitest + RTL auto-
    // cleanup fires after the test file's own afterEach restores
    // the original navigator descriptor — which in jsdom means
    // serviceWorker is deleted). Holding a direct reference to the
    // container we added the listener to means cleanup always
    // operates on a valid target. In production this is identical
    // to re-reading `navigator.serviceWorker`, since the container
    // is a stable singleton on the navigator object.
    const container = navigator.serviceWorker;
    const handleControllerChange = () => {
      if (reloadArmedRef.current) {
        // Reload via the `reload.ts` helper rather than calling
        // `window.location.reload()` directly. Functionally identical
        // in production, but gives the unit test a `vi.spyOn` seam —
        // jsdom marks `location.reload` as non-configurable, so
        // tests cannot redefine it via Object.defineProperty.
        reloadPage();
      }
    };
    container.addEventListener('controllerchange', handleControllerChange);
    return () => {
      container.removeEventListener(
        'controllerchange',
        handleControllerChange,
      );
    };
  }, []);

  const applyUpdate = useCallback(() => {
    if (!waitingWorker) return;
    reloadArmedRef.current = true;
    waitingWorker.postMessage({ type: 'SKIP_WAITING' });
  }, [waitingWorker]);

  return {
    updateAvailable: waitingWorker !== null,
    applyUpdate,
  };
}
